import React, { useContext, useEffect, useRef, useState } from "react";
import { ListGroup } from "react-bootstrap";
import proj4 from "proj4";
import "../css/SearchArea.css";
import { searchAddressEtc } from "../api/mappleLabsSearchApi";
import { MapContext } from "../context/MapContext";

// 文字列を緯度経度の配列に変換
const getLatLonFromStr = (str = "") => {
  const array = str
    .replace(/\t/g, ",") // タブを半角カンマに変換
    .replace(" ", ",") // 半角スペースを半角カンマに変換（改行をコピペすると半角スペースになる）
    .replace(/,{2}/g, ",") // ２回以上繰り返す半角カンマ
    .split(","); // カンマで分割して配列に
  if (array.length === 2 && !isNaN(array[0]) && !isNaN(array[1])) {
    return array;
  }
  return null;
};

const SearchArea = () => {
  const { map } = useContext(MapContext);
  const [inputValue, setInputValue] = useState("");
  const [isEmpty, setIsEmpty] = useState(false);
  const [autoComplete, setAutoComplete] = useState([]); // APIから取得した候補データ
  const [showAutoComplete, setShowAutoComplete] = useState(false); // 候補表示のON/OFF
  const wrapperRef = useRef(null); // クリックイベントを検出するためのラッパー要素の参照
  const isResultClick = useRef(false); // 検索結果をクリックしたかどうか

  useEffect(() => {
    // 入力値が変更されたら候補をフェッチ
    const handler = setTimeout(async () => {
      if (inputValue.length <= 0 || isResultClick.current) {
        // 入力が空、または結果クリックによる変更
        isResultClick.current = false;
        return;
      }
      // 入力が緯度経度のコピペか判定する
      const lonlatArray = getLatLonFromStr(inputValue);
      if (lonlatArray) {
        // 緯度経度に変換できた場合、検索しない
        return;
      }

      const result = await searchAddressEtc(inputValue);
      if (result) {
        setIsEmpty(result.length <= 0); // 検索結果0件

        // グループ化
        const grouped = result.reduce((acc, item) => {
          if (!acc[item.category]) {
            acc[item.category] = [];
          }
          acc[item.category].push(item);
          return acc;
        }, {});
        setShowAutoComplete(true);
        setAutoComplete(grouped);
      }
    }, 500); // デバウンス: 入力後500ms待ってからAPI呼び出し

    return () => {
      clearTimeout(handler);
    };
  }, [inputValue]);

  // 入力フィールド変更ハンドラ
  const handleChange = (e) => {
    setInputValue(e.target.value);
    setIsEmpty(false);
  };
  // 検索結果候補クリック
  const handleItemClick = (item) => {
    isResultClick.current = true;
    setInputValue(item.value);
    setShowAutoComplete(false); // 候補を非表示にする
    // 地図移動
    map.panTo([item.X, item.Y], { zoom: 16 });
  };
  // 外をクリックしたら候補を非表示にする
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowAutoComplete(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // 入力フィールドEnterキー,緯度経度の貼付け対応
  const handleKeyPress = (e) => {
    if (e.which === 13) {
      e.preventDefault();
      // 入力を緯度経度に変換
      const lonlatArray = getLatLonFromStr(inputValue);
      if (lonlatArray) {
        // 変換できた場合、マップ移動
        Func_TKYANDJGD_MAPMOVE(lonlatArray[0], lonlatArray[1]);
      }
    }
  };

  // 地図の移動
  function Func_TKYANDJGD_MAPMOVE(value1, value2) {
    const X = Math.max(value1, value2);
    const Y = Math.min(value1, value2);

    if (X >= 120 && X <= 155 && Y >= 20 && Y <= 140 / 3) {
      map.panTo([X, Y]);
    } else if (
      X >= 432000000 &&
      X <= 558000000 &&
      Y >= 72000000 &&
      Y <= 168000000
    ) {
      const lonlat = proj4(proj4.defs["EPSG:4301"], proj4.defs["EPSG:4326"], {
        x: X / 3600000,
        y: Y / 3600000,
      });
      map.panTo([lonlat.x, lonlat.y]);
    }
  }

  return (
    <div className="searchArea" ref={wrapperRef}>
      <input
        id="search"
        name="search"
        type="text"
        placeholder="目標物・駅・地名等で検索"
        style={{ position: "absolute" }}
        value={inputValue}
        onChange={handleChange}
        onKeyDown={handleKeyPress}
      />
      <p id="empty-message">{isEmpty ? "検索結果は0件です" : ""}</p>
      {/* 候補リストの表示 */}
      {showAutoComplete && Object.keys(autoComplete).length > 0 && (
        <ListGroup className="autocomplete-list">
          {Object.entries(autoComplete).map(([category, items]) => (
            <React.Fragment key={category}>
              {/* カテゴリヘッダー */}
              <ListGroup.Item className="autocomplete-category-header" disabled>
                {category}
              </ListGroup.Item>
              {/* 各候補アイテム */}
              {items.map((item, index) => (
                <ListGroup.Item
                  key={category + "-" + index}
                  action // クリック可能にする
                  onClick={() => handleItemClick(item)}
                  className="autocomplete-item"
                >
                  {item.label}
                </ListGroup.Item>
              ))}
            </React.Fragment>
          ))}
        </ListGroup>
      )}
    </div>
  );
};

export default SearchArea;
